import { useEffect, useState } from "react";
import { banksApi, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Button, Empty, Field, Input, Loader, Modal, Textarea } from "../components/ui";
import { ConfirmDialog, RecordActions } from "../components/actions";
import { COPY } from "../utils/copy";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const emptyForm = { name: "", accountTitle: "", accountNumber: "", note: "" };

const bankToForm = (bank) => ({
  name: bank.name || "",
  accountTitle: bank.accountTitle || "",
  accountNumber: bank.accountNumber || "",
  note: bank.note || "",
});

export default function Banks() {
  const { isBuilder, query, ready } = useBuilderQuery();
  const toast = useToast();
  const { busy, run } = useAsyncAction();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const liveSearch = useDebouncedValue(search);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    if (!ready) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await banksApi.list({ ...query, search: liveSearch });
      setItems(unwrapList(res).items);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSearch, query.builderId]);

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async (event) => {
    event.preventDefault();
    const body = {
      name: form.name.trim(),
      accountTitle: form.accountTitle.trim(),
      accountNumber: form.accountNumber.trim(),
      note: form.note.trim(),
    };
    const result = await run(async () => {
      if (editing) return banksApi.update(editing.id, body);
      return banksApi.create(body);
    }, editing ? "Bank updated." : "Bank added.");

    if (result) {
      closeModal();
      load();
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    const result = await run(() => banksApi.remove(pendingDelete.id), "Bank removed.");
    if (result) {
      setPendingDelete(null);
      load();
    }
  };

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">Your accounts</p>
          <h1>Banks</h1>
        </div>
        {isBuilder ? (
          <Button
            icon="plus"
            onClick={() => {
              setEditing(null);
              setForm(emptyForm);
              setOpen(true);
            }}
          >
            Add bank
          </Button>
        ) : null}
      </header>

      <form className="filters" onSubmit={(event) => event.preventDefault()}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search bank" />
      </form>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty
          title="No banks yet"
          text="Add HBL, Meezan or any account. Cheque, online and pay order will ask which one."
        />
      ) : (
        <div className="sale-grid">
          {items.map((bank) => (
            <article key={bank.id} className="sale-card">
              <div className="sale-card-main">
                <div className="sale-card-top">
                  <h3>{bank.name}</h3>
                </div>
                <p>
                  {[bank.accountTitle, bank.accountNumber].filter(Boolean).join(" · ") || "No account details"}
                </p>
                {bank.note ? <p>{bank.note}</p> : null}
              </div>
              <RecordActions
                onEdit={() => {
                  setEditing(bank);
                  setForm(bankToForm(bank));
                  setOpen(true);
                }}
                onDelete={() => setPendingDelete(bank)}
              />
            </article>
          ))}
        </div>
      )}

      <Modal
        open={open}
        title={editing ? "Edit bank" : "New bank"}
        onClose={closeModal}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" form="bank-form" disabled={busy}>
              Save
            </Button>
          </>
        }
      >
        <form id="bank-form" className="form-grid" onSubmit={save}>
          <Field label="Bank name">
            <Input required minLength={2} maxLength={120} value={form.name} onChange={set("name")} placeholder="HBL" />
          </Field>
          <Field label="Account title">
            <Input maxLength={120} value={form.accountTitle} onChange={set("accountTitle")} placeholder="AA Builder" />
          </Field>
          <Field label="Account number">
            <Input maxLength={40} value={form.accountNumber} onChange={set("accountNumber")} placeholder="1234" />
          </Field>
          <Field label="Note" wide>
            <Textarea maxLength={300} value={form.note} onChange={set("note")} />
          </Field>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete bank?"
        text={COPY.deleteBank}
        busy={busy}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
